import Swiper from "swiper"
import { Navigation, A11y } from "swiper/modules"
import { LucideIconCache, ChevronLeft, ChevronRight } from "./lucide_icon_cache"
import type { GalleryImage } from "./gallery_lightbox"

/**
 * GalleryThumbnails manages the Swiper thumbnail strip for the gallery.
 */
export class GalleryThumbnails {
  private swiper: Swiper | null = null
  private container: HTMLElement
  private images: GalleryImage[]
  private onSelect: (index: number) => void

  constructor(container: HTMLElement, images: GalleryImage[], onSelect: (index: number) => void) {
    this.container = container
    this.images = images
    this.onSelect = onSelect

    this.buildMarkup()
    this.initSwiper()
  }

  /**
   * Build the Swiper HTML structure.
   * Navigation buttons are placed outside the swiper container so they aren't
   * clipped by overflow:hidden (required for proper Swiper boundary detection).
   * Accessibility attributes are handled by Swiper's A11y module.
   */
  private buildMarkup(): void {
    // Icon size 20px (fs-5) per design guide for standard btn-icon buttons
    // Using standard size (38px) instead of btn-sm for better touch targets
    const prevIcon = LucideIconCache.get(ChevronLeft, 20)
    const nextIcon = LucideIconCache.get(ChevronRight, 20)

    this.container.innerHTML = `
      <nav class="gallery-thumbnails-nav" role="navigation" aria-label="Image thumbnails">
        <button type="button" class="swiper-button-prev btn btn-icon btn-lightbox" aria-label="Previous thumbnails">${prevIcon}</button>
        <div class="swiper gallery-thumbnails">
          <div class="swiper-wrapper">
            ${this.images
          .map(
            (img, i) => `
              <button
                type="button"
                class="swiper-slide"
                data-index="${i}"
              >
                <img src="${img.thumb}" alt="Thumbnail ${i + 1}" loading="lazy" />
              </button>
            `
          )
          .join("")}
          </div>
        </div>
        <button type="button" class="swiper-button-next btn btn-icon btn-lightbox" aria-label="Next thumbnails">${nextIcon}</button>
      </nav>
    `
  }

  /**
   * Initialize Swiper for thumbnail navigation.
   */
  private initSwiper(): void {
    const swiperEl = this.container.querySelector(".swiper") as HTMLElement
    if (!swiperEl) return

    // Get direct element references for navigation buttons
    // Buttons are outside the swiper container (in parent nav element) to avoid
    // being clipped by overflow:hidden
    const navEl = this.container.querySelector(".gallery-thumbnails-nav") as HTMLElement
    const prevEl = navEl?.querySelector(".swiper-button-prev") as HTMLElement
    const nextEl = navEl?.querySelector(".swiper-button-next") as HTMLElement

    this.swiper = new Swiper(swiperEl, {
      modules: [Navigation, A11y],
      slidesPerView: "auto",
      spaceBetween: 8,
      slidesPerGroup: 3, // Move up to 3 slides per nav click (Swiper handles edge cases)
      centeredSlides: false,
      slideToClickedSlide: true,
      watchSlidesProgress: true,
      // Observe parent/children for size changes (needed for lazy-loaded images)
      observer: true,
      observeParents: true,
      navigation: {
        nextEl,
        prevEl,
      },
      a11y: {
        prevSlideMessage: "Previous thumbnails",
        nextSlideMessage: "Next thumbnails",
        slideLabelMessage: "View image {{index}} of {{slidesLength}}",
        containerMessage: "Image thumbnails",
        containerRole: "group",
      },
      on: {
        click: (swiper) => {
          // Navigate PhotoSwipe when thumbnail is clicked
          if (swiper.clickedIndex !== undefined) {
            this.onSelect(swiper.clickedIndex)
          }
        },
      },
    })
  }

  /**
   * Sync the thumbnail strip to show the specified index.
   */
  syncTo(index: number): void {
    if (!this.swiper) return

    this.swiper.slideTo(index)
    this.updateActiveThumbnail(index)
  }

  /**
   * Update the active thumbnail highlight and aria-current state.
   */
  private updateActiveThumbnail(index: number): void {
    const slides = this.container.querySelectorAll(".swiper-slide")
    slides.forEach((slide, i) => {
      const isActive = i === index
      slide.classList.toggle("swiper-slide-thumb-active", isActive)
      slide.setAttribute("aria-current", isActive ? "true" : "false")
    })
  }

  /**
   * Clean up Swiper instance.
   */
  destroy(): void {
    if (this.swiper) {
      this.swiper.destroy(true, true)
      this.swiper = null
    }
  }
}
